import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, set, remove } from "firebase/database";
import { FaGift, FaPlus, FaEdit, FaTrash, FaEye, FaDownload, FaCalendar, FaPercent, FaRupeeSign, FaTag } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminOffers.css";
import { setPageMeta } from "../utils/seo";

const AdminOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: "Admin Offers | Banat India",
      description:
        "Create and manage promotional offers in the Banat India admin panel.",
      keywords: "admin offers, Banat India admin, promotions management",
    });
  }, []);

  // Create form state
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    type: "percentage", // percentage, fixed, buy-one-get-one
    value: "",
    minOrderAmount: "",
    maxDiscount: "",
    applicableProducts: [], // all, specific categories, specific products
    startDate: "",
    endDate: "",
    isActive: true,
    usageLimit: "",
    currentUsage: 0
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    type: "percentage",
    value: "",
    minOrderAmount: "",
    maxDiscount: "",
    applicableProducts: [],
    startDate: "",
    endDate: "",
    isActive: true,
    usageLimit: "",
    currentUsage: 0
  });

  const offerTypes = [
    { value: "percentage", label: "Percentage Discount", icon: <FaPercent /> },
    { value: "fixed", label: "Fixed Amount Off", icon: <FaRupeeSign /> },
    { value: "buy-one-get-one", label: "Buy One Get One", icon: <FaGift /> }
  ];

  const productCategories = [
    "children",
    "adult", 
    "old-age",
    "bamboo",
    "charcoal",
    "miswak",
    "whiteness",
    "travel",
    "acrobat",
    "dino",
    "sweepy",
    "ufo"
  ];



  const loadOffers = useCallback(async () => {
    try {
      const database = getDatabase();
      const offersRef = ref(database, "offers");
      
      const snapshot = await get(offersRef);
      
      if (snapshot.exists()) {
        const offersData = snapshot.val();
        const offersList = Object.entries(offersData).map(([id, offer]) => ({
          id,
          ...offer
        }));
        setOffers(offersList);
      } else {
        // Create sample offers if none exist
        await createSampleOffers();
      }
      
      setLoading(false);
    } catch (error) {
      
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initializeOffers = async () => {
      await loadOffers();
    };
    initializeOffers();
  }, [loadOffers]);

  const createSampleOffers = async () => {
    const sampleOffers = [
      {
        id: "offer-1",
        title: "Summer Sale - 20% Off",
        description: "Get 20% off on all toothbrushes this summer",
        type: "percentage",
        value: 20,
        minOrderAmount: 500,
        maxDiscount: 200,
        applicableProducts: ["all"],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isActive: true,
        usageLimit: 100,
        currentUsage: 15,
        createdAt: new Date().toISOString()
      },
      {
        id: "offer-2",
        title: "Kids Collection - ₹50 Off",
        description: "₹50 off on all kids toothbrushes",
        type: "fixed",
        value: 50,
        minOrderAmount: 200,
        maxDiscount: 50,
        applicableProducts: ["children"],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isActive: true,
        usageLimit: 50,
        currentUsage: 8,
        createdAt: new Date().toISOString()
      },
      {
        id: "offer-3",
        title: "Buy 2 Get 1 Free",
        description: "Buy any 2 toothbrushes and get 1 free",
        type: "buy-one-get-one",
        value: 0,
        minOrderAmount: 300,
        maxDiscount: 0,
        applicableProducts: ["adult", "children"],
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isActive: false,
        usageLimit: 25,
        currentUsage: 0,
        createdAt: new Date().toISOString()
      },
      {
        id: "offer-4",
        title: "TRIACTION Special - 15% Off",
        description: "Get 15% off on TRIACTION TOOBRIGHT 511185",
        type: "percentage",
        value: 15,
        minOrderAmount: 100,
        maxDiscount: 50,
        applicableProducts: ["1"], // Single product offer
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isActive: true,
        usageLimit: 30,
        currentUsage: 5,
        createdAt: new Date().toISOString()
      },
      {
        id: "offer-5",
        title: "Kids Collection - ₹30 Off",
        description: "₹30 off on DINO KIDS toothbrush",
        type: "fixed",
        value: 30,
        minOrderAmount: 100,
        maxDiscount: 30,
        applicableProducts: ["9"], // Single product offer
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        isActive: true,
        usageLimit: 40,
        currentUsage: 12,
        createdAt: new Date().toISOString()
      }
    ];

    const database = getDatabase();
    const offersRef = ref(database, "offers");
    
    const offersObject = {};
    sampleOffers.forEach(offer => {
      offersObject[offer.id] = offer;
    });
    
    await set(offersRef, offersObject);
    setOffers(sampleOffers);
  };

  const validateOffer = (offer) => {
    const errors = {};

    if (!offer.title.trim()) {
      errors.title = "Offer title is required";
    }
    if (!offer.description.trim()) {
      errors.description = "Offer description is required";
    }
    if (!offer.value || offer.value <= 0) {
      errors.value = "Offer value must be greater than 0";
    }
    if (offer.type === "percentage" && offer.value > 100) {
      errors.value = "Percentage cannot exceed 100%";
    }
    if (!offer.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!offer.endDate) {
      errors.endDate = "End date is required";
    }
    if (new Date(offer.startDate) >= new Date(offer.endDate)) {
      errors.endDate = "End date must be after start date";
    }
    if (offer.applicableProducts.length === 0) {
      errors.applicableProducts = "At least one product category must be selected";
    }

    return errors;
  };

  const handleCreate = async () => {
    const errors = validateOffer(createForm);
    if (Object.keys(errors).length > 0) {
      alert("Please fix the following errors:\n" + Object.values(errors).join("\n"));
      return;
    }

    setSaving(true);
    
    try {
      const database = getDatabase();
      const offersRef = ref(database, "offers");
      
      // Get current offers
      const snapshot = await get(offersRef);
      const currentOffers = snapshot.exists() ? snapshot.val() : {};
      
      // Create new offer
      const newOffer = {
        ...createForm,
        value: parseFloat(createForm.value),
        minOrderAmount: parseFloat(createForm.minOrderAmount) || 0,
        maxDiscount: parseFloat(createForm.maxDiscount) || 0,
        usageLimit: parseInt(createForm.usageLimit) || 0,
        currentUsage: 0,
        createdAt: new Date().toISOString()
      };
      
      const newId = `offer-${Date.now()}`;
      currentOffers[newId] = newOffer;
      
      await set(offersRef, currentOffers);
      
      // Update local state
      setOffers([...offers, { id: newId, ...newOffer }]);
      
      // Reset form
      setCreateForm({
        title: "",
        description: "",
        type: "percentage",
        value: "",
        minOrderAmount: "",
        maxDiscount: "",
        applicableProducts: [],
        startDate: "",
        endDate: "",
        isActive: true,
        usageLimit: "",
        currentUsage: 0
      });
      
      setShowCreateModal(false);
      alert("Offer created successfully!");
      
    } catch (error) {
      
      alert("Error creating offer");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedOffer) return;

    const errors = validateOffer(editForm);
    if (Object.keys(errors).length > 0) {
      alert("Please fix the following errors:\n" + Object.values(errors).join("\n"));
      return;
    }

    setSaving(true);
    
    try {
      const database = getDatabase();
      const offerRef = ref(database, `offers/${selectedOffer.id}`);
      
      const updatedOffer = {
        ...selectedOffer,
        ...editForm,
        value: parseFloat(editForm.value),
        minOrderAmount: parseFloat(editForm.minOrderAmount) || 0,
        maxDiscount: parseFloat(editForm.maxDiscount) || 0,
        usageLimit: parseInt(editForm.usageLimit) || 0,
        lastUpdated: new Date().toISOString()
      };
      
      await set(offerRef, updatedOffer);
      
      // Update local state
      setOffers(offers.map(offer => 
        offer.id === selectedOffer.id ? { ...offer, ...updatedOffer } : offer
      ));
      
      setShowEditModal(false);
      setSelectedOffer(null);
      alert("Offer updated successfully!");
      
    } catch (error) {
      
      alert("Error updating offer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;

    try {
      const database = getDatabase();
      const offerRef = ref(database, `offers/${offerId}`);
      
      await remove(offerRef);
      
      // Update local state
      setOffers(offers.filter(offer => offer.id !== offerId));
      
      alert("Offer deleted successfully!");
      
    } catch (error) {
      
      alert("Error deleting offer");
    }
  };

  const toggleOfferStatus = async (offerId) => {
    try {
      const database = getDatabase();
      const offerRef = ref(database, `offers/${offerId}`);
      
      const offer = offers.find(o => o.id === offerId);
      const updatedOffer = { ...offer, isActive: !offer.isActive };
      
      await set(offerRef, updatedOffer);
      
      // Update local state
      setOffers(offers.map(offer => 
        offer.id === offerId ? updatedOffer : offer
      ));
      
    } catch (error) {
      
      alert("Error updating offer status");
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || 
      (filterStatus === "active" && offer.isActive) ||
      (filterStatus === "inactive" && !offer.isActive);
    
    return matchesSearch && matchesFilter;
  });

  const getOfferTypeIcon = (type) => {
    const offerType = offerTypes.find(ot => ot.value === type);
    return offerType ? offerType.icon : <FaTag />;
  };

  const getOfferTypeLabel = (type) => {
    const offerType = offerTypes.find(ot => ot.value === type);
    return offerType ? offerType.label : type;
  };

  const getStatusBadge = (offer) => {
    const now = new Date();
    const startDate = new Date(offer.startDate);
    const endDate = new Date(offer.endDate);
    
    if (!offer.isActive) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    
    if (now < startDate) {
      return <span className="status-badge upcoming">Upcoming</span>;
    }
    
    if (now > endDate) {
      return <span className="status-badge expired">Expired</span>;
    }
    
    return <span className="status-badge active">Active</span>;
  };

  const getUsagePercentage = (offer) => {
    if (offer.usageLimit === 0) return 0;
    return Math.round((offer.currentUsage / offer.usageLimit) * 100);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading offers...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content-header">
        <h1>Offer Management</h1>
        <div className="admin-actions">
          <button className="create-btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Create Offer
          </button>
          <button className="export-btn" onClick={() => {
            const data = { offers: offers, exportDate: new Date().toISOString() };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `offers-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}>
            <FaDownload /> Export Offers
          </button>
        </div>
      </div>

      <div className="offers-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search offers by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-box">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Offers</option>
            <option value="active">Active Offers</option>
            <option value="inactive">Inactive Offers</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Offers</h3>
          <p className="stat-number">{offers.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active Offers</h3>
          <p className="stat-number">{offers.filter(offer => offer.isActive).length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Usage</h3>
          <p className="stat-number">{offers.reduce((sum, offer) => sum + offer.currentUsage, 0)}</p>
        </div>
        <div className="stat-card">
          <h3>Avg. Discount</h3>
          <p className="stat-number">
            {offers.length > 0 
              ? Math.round(offers.reduce((sum, offer) => sum + offer.value, 0) / offers.length)
              : 0}%
          </p>
        </div>
      </div>

      <div className="offers-grid">
        {filteredOffers.map((offer) => (
          <div key={offer.id} className="offer-card">
            <div className="offer-header">
              <div className="offer-type">
                {getOfferTypeIcon(offer.type)}
                <span>{getOfferTypeLabel(offer.type)}</span>
              </div>
              {getStatusBadge(offer)}
            </div>
            
            <div className="offer-content">
              <h4>{offer.title}</h4>
              <p>{offer.description}</p>
              
              <div className="offer-details">
                <div className="offer-value">
                  {offer.type === "percentage" && <span>{offer.value}% OFF</span>}
                  {offer.type === "fixed" && <span>₹{offer.value} OFF</span>}
                  {offer.type === "buy-one-get-one" && <span>Buy 2 Get 1 Free</span>}
                </div>
                
                <div className="offer-conditions">
                  <div className="condition">
                    <FaCalendar />
                    <span>Min Order: ₹{offer.minOrderAmount}</span>
                  </div>
                  {offer.maxDiscount > 0 && (
                    <div className="condition">
                      <FaRupeeSign />
                      <span>Max Discount: ₹{offer.maxDiscount}</span>
                    </div>
                  )}
                </div>
                
                <div className="offer-dates">
                  <div className="date">
                    <strong>Start:</strong> {new Date(offer.startDate).toLocaleDateString()}
                  </div>
                  <div className="date">
                    <strong>End:</strong> {new Date(offer.endDate).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="offer-usage">
                  <div className="usage-bar">
                    <div 
                      className="usage-fill"
                      style={{ width: `${getUsagePercentage(offer)}%` }}
                    ></div>
                  </div>
                  <span className="usage-text">
                    {offer.currentUsage} / {offer.usageLimit} used
                  </span>
                </div>
              </div>
            </div>
            
            <div className="offer-actions">
              <button 
                className="action-btn view-btn"
                onClick={() => alert(`Offer Details:\n${JSON.stringify(offer, null, 2)}`)}
              >
                <FaEye /> View
              </button>
              <button 
                className="action-btn edit-btn"
                onClick={() => {
                  setSelectedOffer(offer);
                  setEditForm({
                    title: offer.title,
                    description: offer.description,
                    type: offer.type,
                    value: offer.value.toString(),
                    minOrderAmount: offer.minOrderAmount.toString(),
                    maxDiscount: offer.maxDiscount.toString(),
                    applicableProducts: offer.applicableProducts,
                    startDate: offer.startDate,
                    endDate: offer.endDate,
                    isActive: offer.isActive,
                    usageLimit: offer.usageLimit.toString(),
                    currentUsage: offer.currentUsage
                  });
                  setShowEditModal(true);
                }}
              >
                <FaEdit /> Edit
              </button>
              <button 
                className="action-btn toggle-btn"
                onClick={() => toggleOfferStatus(offer.id)}
                style={{ 
                  background: offer.isActive ? '#dc3545' : '#28a745',
                  color: 'white'
                }}
              >
                {offer.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button 
                className="action-btn delete-btn"
                onClick={() => handleDelete(offer.id)}
              >
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredOffers.length === 0 && (
        <div className="no-offers">
          <FaGift />
          <p>No offers found matching your criteria.</p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Offer</h3>
            <div className="create-form">
              <div className="form-group">
                <label>Offer Title *</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                  placeholder="Enter offer title"
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                  placeholder="Enter offer description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Offer Type *</label>
                <select
                  value={createForm.type}
                  onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                >
                  {offerTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Offer Value *</label>
                <input
                  type="number"
                  value={createForm.value}
                  onChange={(e) => setCreateForm({...createForm, value: e.target.value})}
                  placeholder={createForm.type === "percentage" ? "Enter percentage" : "Enter amount"}
                  min="0"
                  max={createForm.type === "percentage" ? "100" : ""}
                />
              </div>
              <div className="form-group">
                <label>Minimum Order Amount</label>
                <input
                  type="number"
                  value={createForm.minOrderAmount}
                  onChange={(e) => setCreateForm({...createForm, minOrderAmount: e.target.value})}
                  placeholder="Enter minimum order amount"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Maximum Discount</label>
                <input
                  type="number"
                  value={createForm.maxDiscount}
                  onChange={(e) => setCreateForm({...createForm, maxDiscount: e.target.value})}
                  placeholder="Enter maximum discount"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Applicable Products</label>
                <div className="checkbox-group">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={createForm.applicableProducts.includes("all")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCreateForm({...createForm, applicableProducts: ["all"]});
                        } else {
                          setCreateForm({...createForm, applicableProducts: []});
                        }
                      }}
                    />
                    All Products
                  </label>
                  {productCategories.map(category => (
                    <label key={category} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={createForm.applicableProducts.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCreateForm({
                              ...createForm, 
                              applicableProducts: [...createForm.applicableProducts.filter(p => p !== "all"), category]
                            });
                          } else {
                            setCreateForm({
                              ...createForm, 
                              applicableProducts: createForm.applicableProducts.filter(p => p !== category)
                            });
                          }
                        }}
                        disabled={createForm.applicableProducts.includes("all")}
                      />
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={createForm.startDate}
                    onChange={(e) => setCreateForm({...createForm, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={createForm.endDate}
                    onChange={(e) => setCreateForm({...createForm, endDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Usage Limit</label>
                <input
                  type="number"
                  value={createForm.usageLimit}
                  onChange={(e) => setCreateForm({...createForm, usageLimit: e.target.value})}
                  placeholder="Enter usage limit (0 for unlimited)"
                  min="0"
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={createForm.isActive}
                    onChange={(e) => setCreateForm({...createForm, isActive: e.target.checked})}
                  />
                  Active Offer
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="create-btn"
                onClick={handleCreate}
                disabled={saving}
              >
                {saving ? "Creating..." : "Create Offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOffer && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Offer</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Offer Title *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  placeholder="Enter offer title"
                />
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder="Enter offer description"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Offer Type *</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({...editForm, type: e.target.value})}
                >
                  {offerTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Offer Value *</label>
                <input
                  type="number"
                  value={editForm.value}
                  onChange={(e) => setEditForm({...editForm, value: e.target.value})}
                  placeholder={editForm.type === "percentage" ? "Enter percentage" : "Enter amount"}
                  min="0"
                  max={editForm.type === "percentage" ? "100" : ""}
                />
              </div>
              <div className="form-group">
                <label>Minimum Order Amount</label>
                <input
                  type="number"
                  value={editForm.minOrderAmount}
                  onChange={(e) => setEditForm({...editForm, minOrderAmount: e.target.value})}
                  placeholder="Enter minimum order amount"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Maximum Discount</label>
                <input
                  type="number"
                  value={editForm.maxDiscount}
                  onChange={(e) => setEditForm({...editForm, maxDiscount: e.target.value})}
                  placeholder="Enter maximum discount"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Applicable Products</label>
                <div className="checkbox-group">
                  <label className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={editForm.applicableProducts.includes("all")}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditForm({...editForm, applicableProducts: ["all"]});
                        } else {
                          setEditForm({...editForm, applicableProducts: []});
                        }
                      }}
                    />
                    All Products
                  </label>
                  {productCategories.map(category => (
                    <label key={category} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={editForm.applicableProducts.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditForm({
                              ...editForm, 
                              applicableProducts: [...editForm.applicableProducts.filter(p => p !== "all"), category]
                            });
                          } else {
                            setEditForm({
                              ...editForm, 
                              applicableProducts: editForm.applicableProducts.filter(p => p !== category)
                            });
                          }
                        }}
                        disabled={editForm.applicableProducts.includes("all")}
                      />
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={editForm.startDate}
                    onChange={(e) => setEditForm({...editForm, startDate: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={editForm.endDate}
                    onChange={(e) => setEditForm({...editForm, endDate: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Usage Limit</label>
                <input
                  type="number"
                  value={editForm.usageLimit}
                  onChange={(e) => setEditForm({...editForm, usageLimit: e.target.value})}
                  placeholder="Enter usage limit (0 for unlimited)"
                  min="0"
                />
              </div>
              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                  />
                  Active Offer
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button 
                className="edit-btn"
                onClick={handleEdit}
                disabled={saving}
              >
                {saving ? "Updating..." : "Update Offer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOffers; 